import {observer} from 'mobx-react';
import {parseCurrencyValue} from '@constants';
import {useStoreContext} from '@store/store-context';
import {Button} from '@ui/atoms/button';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {Headline4, Body2} from '@ui/atoms/typography';
import dayjs from 'dayjs';
import {Modal} from '@ui/molecules/modal';
import {CancelIcon} from '@ui/atoms/icons';
import {capitalize} from '@utils';
import {NoBorderButton} from '@pages/dashboard/reporting/reporting-pages/reporting-overview/styles';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {AdminBillingRequestImpl} from '../../admin-billing.request';
import {activateNotification} from '@ui/molecules/notification/activate-notification';
import {AdminBillingController} from '../../admin-billing.controller';
import {useEffect, useMemo, useState} from 'react';
import {RotatingLines} from 'react-loader-spinner';
import {AlertBadgeErrorSVG} from '@ui/atoms/svg';
import {generateRandomDigits} from '@utils/generate-random-numbers';
import {isPaymentRequired} from '@utils/billing-state';
import {UpdateCard} from './components/update-card';
import {AnnualModal} from './components/annual-modal';
import {useBillingHook} from '../../admin-billing.hooks';
import {ElementsConsumer} from '@stripe/react-stripe-js';
import {TextField} from '@ui/molecules/field/textfield';
import {
  PlanDiv,
  Wrapper,
  InfoContainer,
  CardDetailsDiv,
  RetryPaymentNotificationDiv,
  PaymentHistoryDiv,
  SeatDetails,
  SubscriptionCardTitle,
  SubscriptionRow,
  SubscriptionCopy,
  SubscriptionLabel,
  SubscriptionDescription,
  SubscriptionControl,
  PlanOverview,
  StatusPill,
  PlanName,
  PlanPrice,
  PlanActions,
  PaymentDate,
  PaymentMethodPanel,
  PaymentCardName,
  PaymentCardMeta,
  HistoryRow,
  HistoryMeta,
  PaymentStatusPill,
  HistoryControl,
  HistoryAmount,
  ReceiptLink,
  EmptyBillingState,
  SeatModalPositioner,
  SeatDialogShell,
  SeatDialogHeader,
  SeatDialogTitle,
  SeatDialogBody,
  SeatDialogDescription,
  SeatDialogNotice,
  SeatDialogField,
  SeatDialogCapacity,
  SeatDialogFooter,
  BillingActivityDescription,
  BillingSupportLink,
  CancellationDialogShell,
  CancellationDialogHeader,
  CancellationDialogTitle,
  CancellationDialogBody,
  CancellationDialogDescription,
  CancellationDialogNotice,
  CancellationDialogFooter,
  DestructiveButton,
} from './billing-active.styles';
import {setFirebaseData} from '@utils/firebase-handler';
import {getBillingActivityPresentation} from './billing-activity';
dayjs.extend(advancedFormat);

const formatBillingAmount = (amount?: number | string) => {
  const numericAmount = Number(amount);
  return Number.isFinite(numericAmount)
    ? numericAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : amount || '—';
};

export const AdminBillingActive = observer(() => {
  const {
    billingsStore: {
      billingSubscriptions,
      billingMethods,
      nextCycle,
      setBillingSubscriptions,
      billingPlans,
    },
    storeDashboardRoutes: {activateWorkspaceTab},
    billingRoutesStore: {activateInactiveBillingTabs},
    authStore: {auth, featureLimitStatus, setBillingInAuth},
    usersStore: {users, allUsers},
  } = useStoreContext();

  const {loadBillingSubscriptions, nextCycleRefetch} = useBillingHook();

  const workspaceBilling = auth.user.workspace.billing;

  const plansSwitched = workspaceBilling.switchPlan;

  const isLifetimePlan = workspaceBilling.plan === 'lifetime';

  const isSubscriptionCanceled = !auth.user.workspace.billing.renew;

  const activeSubscriptionPlan = billingSubscriptions?.results?.find(
    (billingSubscription: any) => billingSubscription?.status === 'active',
  );

  const latestBillingStatus =
    billingSubscriptions?.results?.[0]?.status?.trim().toLowerCase() || '';
  const isCurrentSubscriptionPending =
    isPaymentRequired(workspaceBilling) ||
    ['pending', 'past_due', 'unpaid', 'failed', 'payment_failed'].includes(
      latestBillingStatus,
    );

  const occupiedSeats = useMemo(() => {
    const reportedSeats = Number(featureLimitStatus?.currentStatus?.seats);
    if (Number.isFinite(reportedSeats) && reportedSeats > 0)
      return reportedSeats;

    return Math.max(
      1,
      (allUsers?.length ? allUsers : users || []).filter(
        (user: any) =>
          user?.status !== 'deactivated' && user?.status !== 'suspended',
      ).length,
    );
  }, [allUsers, featureLimitStatus?.currentStatus?.seats, users]);
  const billableSeats = Math.max(
    occupiedSeats,
    Number(nextCycle?.seats || workspaceBilling.seats) || occupiedSeats,
  );

  const [showPaymentError, setShowPaymentError] = useState(true);

  const [openDrawer, setOpenDrawer] = useState(false);

  const [openUpgradeSub, setOpenUpgradeSub] = useState(false);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [updatingSeats, setUpdatingSeats] = useState(false);
  const [seatError, setSeatError] = useState('');
  const [seatCount, setSeatCount] = useState(billableSeats);
  const seatCountIsValid =
    Number.isInteger(seatCount) && seatCount >= occupiedSeats;
  const availableSeats = seatCountIsValid ? seatCount - occupiedSeats : 0;

  const [loading, setLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState<'free' | '' | 'paid'>(
    '',
  );

  const isCancelFreeTrial = showCancelModal === 'free';

  const [retrying, setRetrying] = useState(false);

  const cancelSubscription = async () => {
    setLoading(true);
    const request = new AdminBillingRequestImpl();
    const controller = new AdminBillingController(request);

    try {
      const response = await controller.cancelSubscription();
      if (!response) return;

      const updatedBilling = {...workspaceBilling, renew: false};
      setBillingInAuth(updatedBilling);

      const storedAuth = localStorage.getItem('auth');
      if (storedAuth) {
        const parsedAuth = JSON.parse(storedAuth);
        localStorage.setItem(
          'auth',
          JSON.stringify({
            ...parsedAuth,
            user: {
              ...parsedAuth.user,
              workspace: {
                ...parsedAuth.user?.workspace,
                billing: updatedBilling,
              },
            },
          }),
        );
      }

      setShowCancelModal('');
      activateNotification({
        title: 'Cancellation scheduled',
        content: isCancelFreeTrial
          ? 'Your free trial was cancelled.'
          : validCycleEndDate
          ? `Your subscription will end on ${formattedCycleEndDate}.`
          : 'Your subscription will not renew at the end of the current billing period.',
        kind: 'success',
      });
      activateWorkspaceTab('billing');
      setFirebaseData(`updates/settings`, generateRandomDigits(16));
      await Promise.all([nextCycleRefetch(), loadBillingSubscriptions()]);
      activateInactiveBillingTabs('plans');
    } finally {
      setLoading(false);
    }
  };

  const computePlanName = (plan: string = '') => {
    if (plan === 'lifetime') return 'Lifetime';

    return 'Standard';
  };

  const activePlan = computePlanName(workspaceBilling?.plan);
  const subscriptionAmount =
    nextCycle?.amount || billingSubscriptions?.results?.[0]?.amount?.cost;
  const formattedSubscriptionAmount = formatBillingAmount(subscriptionAmount);

  const [modalAction] = useState<'upgrade' | 'downgrade' | ''>('');

  const cycleEndDate = workspaceBilling?.cycle?.endDate;
  const validCycleEndDate = Boolean(
    cycleEndDate && dayjs(cycleEndDate).isValid(),
  );
  const formattedCycleEndDate = validCycleEndDate
    ? dayjs(cycleEndDate).format('MMMM Do, YYYY')
    : '';
  const showBillingCycleDate = !isLifetimePlan && validCycleEndDate;
  const planStatus = isCurrentSubscriptionPending
    ? 'Payment required'
    : isSubscriptionCanceled
    ? 'Cancellation scheduled'
    : 'Active';

  const retrySubscription = async () => {
    setRetrying(true);
    const request = new AdminBillingRequestImpl();

    const controller = new AdminBillingController(request);

    const response = await controller.retrySubscription();

    response &&
      activateNotification({
        title: 'Success',
        content: 'Payment successful.',
        kind: 'success',
      });

    if (response) {
      window.location.reload();

      const request = new AdminBillingRequestImpl();
      const controller = new AdminBillingController(request);

      const sub = await controller.fetchAllBillingSubscriptions();
      sub && setBillingSubscriptions(sub);
    }
    response && setFirebaseData(`updates/settings`, generateRandomDigits(16));

    response && loadBillingSubscriptions();
    response && activateInactiveBillingTabs('plans');

    setRetrying(false);
  };

  const openSeatManagement = () => {
    setSeatCount(billableSeats);
    setSeatError('');
    setShowSeatModal(true);
  };

  const closeSeatManagement = () => {
    if (!updatingSeats) setShowSeatModal(false);
  };

  useEffect(() => {
    if (!showSeatModal) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !updatingSeats) setShowSeatModal(false);
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [showSeatModal, updatingSeats]);

  const updateSeats = async () => {
    if (!Number.isInteger(seatCount) || seatCount < occupiedSeats) {
      setSeatError(
        `Enter at least ${occupiedSeats} seat${
          occupiedSeats === 1 ? '' : 's'
        } for your current members.`,
      );
      return;
    }

    if (!activeSubscriptionPlan?.id) {
      setSeatError('We could not find an active subscription to update.');
      return;
    }

    setUpdatingSeats(true);
    const controller = new AdminBillingController(
      new AdminBillingRequestImpl(),
    );
    try {
      const response = await controller.editSpecificBillingSubscription(
        activeSubscriptionPlan.id,
        {seats: String(seatCount)},
      );

      if (response) {
        const updatedBilling = {...workspaceBilling, seats: seatCount};
        setBillingInAuth(updatedBilling);

        const storedAuth = localStorage.getItem('auth');
        if (storedAuth) {
          const parsedAuth = JSON.parse(storedAuth);
          localStorage.setItem(
            'auth',
            JSON.stringify({
              ...parsedAuth,
              user: {
                ...parsedAuth.user,
                workspace: {
                  ...parsedAuth.user?.workspace,
                  billing: updatedBilling,
                },
              },
            }),
          );
        }

        await Promise.all([loadBillingSubscriptions(), nextCycleRefetch()]);
        setFirebaseData(`updates/settings`, generateRandomDigits(16));
        setShowSeatModal(false);
      }
    } finally {
      setUpdatingSeats(false);
    }
  };

  return (
    <>
      {showPaymentError && isCurrentSubscriptionPending && (
        <RetryPaymentNotificationDiv>
          <AlertBadgeErrorSVG />
          {'    '}
          <Body2 weight="bold" kind="alwaysWhite" style={{marginLeft: '8px'}}>
            There are issues with your payment method.{' '}
            <span>
              {' '}
              <NoBorderButton disabled={retrying} onClick={retrySubscription}>
                <span style={{textDecoration: 'underline', fontWeight: 600}}>
                  {retrying ? 'Retrying ...' : '  Retry payment'}
                </span>
              </NoBorderButton>
            </span>{' '}
            or{' '}
            <span>
              {' '}
              <NoBorderButton onClick={() => setOpenDrawer(true)}>
                <span style={{textDecoration: 'underline', fontWeight: 600}}>
                  Update your card
                </span>
              </NoBorderButton>
            </span>
          </Body2>
          <NoBorderButton
            style={{marginLeft: '5px', marginTop: '3px'}}
            onClick={() => setShowPaymentError(false)}>
            <CancelIcon color={'white'} />
          </NoBorderButton>
        </RetryPaymentNotificationDiv>
      )}

      <Wrapper>
        {plansSwitched && !isSubscriptionCanceled && (
          <InfoContainer>
            Your previous plan is still active. Starting{' '}
            {dayjs(nextCycle?.startDate).format('MMMM Do')}, you will be charged
            for the new plan.
          </InfoContainer>
        )}

        <PlanDiv>
          <SubscriptionCardTitle>Subscription</SubscriptionCardTitle>
          <SubscriptionRow>
            <SubscriptionCopy>
              <SubscriptionLabel>Current plan</SubscriptionLabel>
              <SubscriptionDescription>
                Your workspace is on the {activePlan} plan.
              </SubscriptionDescription>
            </SubscriptionCopy>
            <SubscriptionControl>
              <PlanOverview>
                <div>
                  <StatusPill
                    $ending={isSubscriptionCanceled}
                    $warning={isCurrentSubscriptionPending}>
                    {planStatus}
                  </StatusPill>
                  <PlanName>{activePlan}</PlanName>
                </div>
                <PlanPrice>
                  <strong>
                    {parseCurrencyValue(
                      activeSubscriptionPlan?.amount?.currency,
                    )}
                    {formattedSubscriptionAmount}
                  </strong>
                  <span>
                    {isLifetimePlan
                      ? 'one-time payment'
                      : workspaceBilling?.plan.includes('yearly')
                      ? 'per year'
                      : 'per month'}
                  </span>
                </PlanPrice>
              </PlanOverview>
              {!isSubscriptionCanceled && !isCurrentSubscriptionPending && (
                <PlanActions>
                  {workspaceBilling?.plan === 'monthly' && (
                    <Button
                      style={{padding: '8px 16px'}}
                      kind="secondary"
                      disabled={plansSwitched}
                      onClick={() => setOpenUpgradeSub(true)}>
                      Switch to annual
                    </Button>
                  )}
                  {!isLifetimePlan && (
                    <NoBorderButton onClick={() => setShowCancelModal('paid')}>
                      <Body2 weight="semibold" kind="red400">
                        Cancel subscription
                      </Body2>
                    </NoBorderButton>
                  )}
                </PlanActions>
              )}
            </SubscriptionControl>
          </SubscriptionRow>
          {!isCurrentSubscriptionPending && (
            <SubscriptionRow>
              <SubscriptionCopy>
                <SubscriptionLabel>Seats</SubscriptionLabel>
                <SubscriptionDescription>
                  Active members occupy seats. Extra seats leave room for new
                  teammates.
                </SubscriptionDescription>
              </SubscriptionCopy>
              <SubscriptionControl>
                <SeatDetails>
                  <div className="seatMetric">
                    <Body2 kind="textBody">Active members</Body2>
                    <VerticalSpacer size="4px" />
                    <Headline4 kind="textDark">{occupiedSeats}</Headline4>
                  </div>
                  <div className="seatMetric">
                    <Body2 kind="textBody">Billable seats</Body2>
                    <VerticalSpacer size="4px" />
                    <Headline4 kind="textDark">{billableSeats}</Headline4>
                  </div>
                </SeatDetails>
                <PlanActions>
                  <Button
                    kind="secondary"
                    style={{padding: '8px 16px'}}
                    disabled={isSubscriptionCanceled || isLifetimePlan}
                    onClick={openSeatManagement}>
                    Manage seats
                  </Button>
                </PlanActions>
              </SubscriptionControl>
            </SubscriptionRow>
          )}
          {showBillingCycleDate && !isCurrentSubscriptionPending && (
            <SubscriptionRow>
              <SubscriptionCopy>
                <SubscriptionLabel>
                  {isSubscriptionCanceled ? 'Access until' : 'Next payment'}
                </SubscriptionLabel>
                <SubscriptionDescription>
                  {isSubscriptionCanceled
                    ? 'Your subscription will not renew after this date.'
                    : 'Your subscription renews automatically on this date.'}
                </SubscriptionDescription>
              </SubscriptionCopy>
              <PaymentDate>{formattedCycleEndDate}</PaymentDate>
            </SubscriptionRow>
          )}
          {isSubscriptionCanceled &&
            !isCurrentSubscriptionPending &&
            !validCycleEndDate && (
              <SubscriptionRow>
                <SubscriptionCopy>
                  <SubscriptionLabel>Access end date</SubscriptionLabel>
                  <SubscriptionDescription>
                    Your subscription will not renew. Contact support to confirm
                    the exact date your paid access ends.
                  </SubscriptionDescription>
                </SubscriptionCopy>
                <PaymentDate>Not available</PaymentDate>
              </SubscriptionRow>
            )}
        </PlanDiv>

        {(!isSubscriptionCanceled || isCurrentSubscriptionPending) && (
          <CardDetailsDiv>
            <SubscriptionCardTitle>Payment method</SubscriptionCardTitle>
            {billingMethods?.data?.length ? (
              billingMethods.data.map((billingMethod: any, index: number) => (
                <SubscriptionRow key={billingMethod?.id}>
                  <SubscriptionCopy>
                    <SubscriptionLabel>
                      {index === 0 ? 'Primary card' : 'Saved card'}
                    </SubscriptionLabel>
                    <SubscriptionDescription>
                      {index === 0
                        ? 'Used for subscription renewals and seat changes.'
                        : 'A saved payment method for this workspace.'}
                    </SubscriptionDescription>
                  </SubscriptionCopy>
                  <SubscriptionControl>
                    <PaymentMethodPanel>
                      <div>
                        <PaymentCardName>
                          {capitalize(billingMethod?.card?.brand)} ••••{' '}
                          {billingMethod?.card?.last4}
                        </PaymentCardName>
                        <PaymentCardMeta>
                          Expires{' '}
                          {dayjs(
                            `2000-${billingMethod?.card?.exp_month}-01`,
                          ).format('MMMM')}{' '}
                          {billingMethod?.card?.exp_year}
                        </PaymentCardMeta>
                      </div>
                      <Button
                        kind="secondary"
                        style={{padding: '8px 16px'}}
                        onClick={() => setOpenDrawer(true)}>
                        Update card
                      </Button>
                    </PaymentMethodPanel>
                  </SubscriptionControl>
                </SubscriptionRow>
              ))
            ) : (
              <SubscriptionRow>
                <SubscriptionCopy>
                  <SubscriptionLabel>No payment method</SubscriptionLabel>
                  <SubscriptionDescription>
                    Add a card for subscription renewals and seat changes.
                  </SubscriptionDescription>
                </SubscriptionCopy>
                <PlanActions>
                  <Button onClick={() => setOpenDrawer(true)}>Add card</Button>
                </PlanActions>
              </SubscriptionRow>
            )}
          </CardDetailsDiv>
        )}

        <PaymentHistoryDiv>
          <SubscriptionCardTitle>Billing activity</SubscriptionCardTitle>
          <BillingActivityDescription>
            Subscription records and payment attempts for this workspace. Need
            an invoice or receipt?{' '}
            <BillingSupportLink href="mailto:hi@peoplebeam.com">
              Email hi@peoplebeam.com
            </BillingSupportLink>
            .
          </BillingActivityDescription>
          {billingSubscriptions?.results?.length ? (
            billingSubscriptions.results.map(
              (billingSubscription: any, idx: number) => {
                const activityStatus = getBillingActivityPresentation(
                  billingSubscription?.status,
                );
                const activityDate = dayjs(
                  billingSubscription?.createdAt,
                ).format('MMMM DD, YYYY');

                return (
                  <HistoryRow key={billingSubscription?.id}>
                    <SubscriptionCopy>
                      <SubscriptionLabel>{activityDate}</SubscriptionLabel>
                      <HistoryMeta>
                        <PaymentStatusPill $tone={activityStatus.tone}>
                          {activityStatus.label}
                        </PaymentStatusPill>
                        {idx === 0 && isCurrentSubscriptionPending && (
                          <>
                            {retrying ? (
                              <RotatingLines width="16" strokeColor="#585ADF" />
                            ) : (
                              <NoBorderButton onClick={retrySubscription}>
                                <Body2 kind="purple300" weight="semibold">
                                  Retry payment
                                </Body2>
                              </NoBorderButton>
                            )}
                          </>
                        )}
                      </HistoryMeta>
                    </SubscriptionCopy>
                    <HistoryControl>
                      <HistoryAmount>
                        <span>Amount</span>
                        <strong>
                          {parseCurrencyValue(
                            billingSubscription?.amount?.currency,
                          )}
                          {formatBillingAmount(
                            billingSubscription?.amount?.cost,
                          )}
                        </strong>
                        {billingSubscription?.paymentInfo?.receiptUrl && (
                          <ReceiptLink
                            aria-label={`View receipt from ${activityDate}`}
                            href={billingSubscription?.paymentInfo?.receiptUrl}
                            target="_blank"
                            rel="noreferrer">
                            View receipt
                          </ReceiptLink>
                        )}
                      </HistoryAmount>
                    </HistoryControl>
                  </HistoryRow>
                );
              },
            )
          ) : (
            <EmptyBillingState>
              No billing activity has been recorded yet.
            </EmptyBillingState>
          )}
        </PaymentHistoryDiv>

        <Modal open={!!showCancelModal} onClose={() => setShowCancelModal('')}>
          <SeatModalPositioner>
            <CancellationDialogShell
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancel-subscription-title"
              onSubmit={(event) => {
                event.preventDefault();
                cancelSubscription();
              }}>
              <CancellationDialogHeader>
                <CancellationDialogTitle id="cancel-subscription-title">
                  Cancel {isCancelFreeTrial ? 'trial' : 'subscription'}?
                </CancellationDialogTitle>
              </CancellationDialogHeader>
              <CancellationDialogBody>
                <CancellationDialogDescription>
                  {isCancelFreeTrial
                    ? 'Your workspace access will end when the trial expires. There is no free plan.'
                    : 'Your plan will stop renewing automatically. When the current paid term expires, you will lose access to the workspace and its information.'}
                </CancellationDialogDescription>
                <CancellationDialogNotice>
                  {validCycleEndDate ? (
                    <>
                      You can continue using this workspace until{' '}
                      <strong>{formattedCycleEndDate}</strong>. After that, you
                      will no longer be able to access the workspace or its
                      information.
                    </>
                  ) : (
                    <>
                      Your access continues until the end of the current paid
                      term. The exact date is not available, so contact support
                      before cancelling if you need it confirmed. When the term
                      ends, you will no longer be able to access the workspace
                      or its information.
                    </>
                  )}
                </CancellationDialogNotice>
                {isCurrentSubscriptionPending && (
                  <CancellationDialogNotice $warning>
                    <strong>
                      An outstanding payment still needs attention.
                    </strong>{' '}
                    Cancelling renewal does not clear that payment attempt.
                  </CancellationDialogNotice>
                )}
              </CancellationDialogBody>
              <CancellationDialogFooter>
                <Button
                  type="button"
                  kind="secondary"
                  disabled={loading}
                  onClick={() => setShowCancelModal('')}>
                  Keep subscription
                </Button>
                <DestructiveButton
                  type="submit"
                  isLoading={loading}
                  disabled={loading}>
                  Cancel {isCancelFreeTrial ? 'trial' : 'subscription'}
                </DestructiveButton>
              </CancellationDialogFooter>
            </CancellationDialogShell>
          </SeatModalPositioner>
        </Modal>
        <Modal open={showSeatModal} onClose={closeSeatManagement} fullHeight>
          <SeatModalPositioner>
            <SeatDialogShell
              role="dialog"
              aria-modal="true"
              aria-labelledby="manage-seats-title"
              onSubmit={(event) => {
                event.preventDefault();
                updateSeats();
              }}>
              <SeatDialogHeader>
                <SeatDialogTitle id="manage-seats-title">
                  Manage seats
                </SeatDialogTitle>
              </SeatDialogHeader>
              <SeatDialogBody>
                <SeatDialogDescription>
                  Choose the total number of seats available in your workspace.
                </SeatDialogDescription>
                <SeatDialogNotice>
                  You have {occupiedSeats} active member
                  {occupiedSeats === 1 ? '' : 's'}, so your seat total cannot be
                  lower than {occupiedSeats}. Increasing seats may add a
                  prorated charge for the current billing period.
                </SeatDialogNotice>
                <SeatDialogField>
                  <TextField
                    autoFocus
                    label="Total seats"
                    type="number"
                    min={occupiedSeats}
                    step={1}
                    value={seatCount}
                    onChange={(event) => {
                      const nextSeatCount = Number(event.target.value);
                      setSeatCount(nextSeatCount);
                      setSeatError(
                        Number.isInteger(nextSeatCount) &&
                          nextSeatCount >= occupiedSeats
                          ? ''
                          : `Enter at least ${occupiedSeats} seat${
                              occupiedSeats === 1 ? '' : 's'
                            } for your current members.`,
                      );
                    }}
                    helper={seatError}
                    state={seatError ? 'error' : 'default'}
                  />
                  <SeatDialogCapacity aria-live="polite">
                    {seatCountIsValid
                      ? `${availableSeats} open seat${
                          availableSeats === 1 ? '' : 's'
                        } after your current members.`
                      : `At least ${occupiedSeats} seats required.`}
                  </SeatDialogCapacity>
                </SeatDialogField>
              </SeatDialogBody>
              <SeatDialogFooter>
                <Button
                  type="button"
                  kind="secondary"
                  disabled={updatingSeats}
                  onClick={closeSeatManagement}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={updatingSeats}
                  disabled={
                    updatingSeats ||
                    !seatCountIsValid ||
                    seatCount === billableSeats
                  }>
                  Update seats
                </Button>
              </SeatDialogFooter>
            </SeatDialogShell>
          </SeatModalPositioner>
        </Modal>
        <ElementsConsumer>
          {({stripe, elements}) => (
            <UpdateCard
              open={openDrawer}
              onClose={() => setOpenDrawer(false)}
              stripe={stripe}
              elements={elements}
            />
          )}
        </ElementsConsumer>
        <AnnualModal
          open={openUpgradeSub}
          type={modalAction}
          billingPlans={billingPlans}
          billingType={activeSubscriptionPlan?.plan}
          currency={nextCycle?.currency || ''}
          seats={nextCycle?.seats || 0}
          loadBillingSubscriptions={loadBillingSubscriptions}
          nextStartDate={nextCycle?.startDate}
          monthlyCost={nextCycle?.amount}
          onClose={() => setOpenUpgradeSub(false)}
        />
      </Wrapper>
    </>
  );
});
